import { LoaderTalos } from '@talos/kyoko';
import lazyWithPrefetch from '@talos/kyoko/src/utils/lazyWithPrefetch';
import type { GroupviewPanelState } from 'dockview';
import { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { PANEL_COMPONENTS_MAP } from '../../../components/AppLayout/Panels';
import { getLayoutGroupById } from '../../../providers/AppLayoutContextProvider/utils/getLayoutGroupById';
import type { LayoutSpec, PanelType } from '../../../types/LayoutConfig';
import { MobileBalancesBlotter } from './Blotters/MobileBalancesBlotter';
import { MobileCreditBlotter } from './Blotters/MobileCreditBlotter';
// import { MobileHedgePositionStatusBlotter } from './Blotters/MobileHedgePositionStatusBlotter';
// import { MobileLoansBlotter } from './Blotters/MobileLoansBlotter';
import { MobileOrdersBlotter } from './Blotters/MobileOrdersBlotter';
// import { MobileOTCDerivativesBlotter } from './Blotters/MobileOTCDerivativesBlotter';
import { MobilePositionsBlotter } from './Blotters/MobilePositionsBlotter';
// import { MobileReconMismatchesBlotter } from './Blotters/MobileReconMismatchesBlotter';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { MobileTradesBlotter } from './Blotters/MobileTradesBlotter';
import { MobileTransfersBlotter } from './Blotters/MobileTransfersBlotter';
import { getTradingPanelRoute } from './Blotters/navRoutes';
import { MobileMarketPanel } from './Markets/MobileMarketPanel';
import { isEnabledMarketTabPanelType } from './Markets/marketTabPanelTypes';
import {
  NO_MOBILE_FRIENDLY_PANELS_MESSAGE,
  NO_MOBILE_FRIENDLY_PANELS_TITLE,
  TradingPlaceholder,
} from './TradingPlaceholder';
import { getPanelConfig } from './utils/getPanelConfig';
import { MOBILE_SUPPORTED_PANEL_TYPES } from './utils/mobileSupportedPanelTypes';

const MobileMarketCards = lazyWithPrefetch(() => import('@src/containers/Mobile/Trading/Markets/MobileMarketCards'));
const MobilePredictionMarkets = lazyWithPrefetch(
  () => import('@src/components/PredictionMarkets/MobilePredictionMarkets')
);
const MobileWatchlistPanel = lazyWithPrefetch(
  () => import('@src/containers/Mobile/Trading/Markets/MobileWatchlistPanel')
);

MobileMarketCards.prefetch();
MobilePredictionMarkets.prefetch();
MobileWatchlistPanel.prefetch();

const TRADING_SECTION_ID = 'trading';
type MobileFriendlyPanelScope = 'market' | 'blotter' | 'any';
type DockviewLayoutWithActiveGroup = LayoutSpec['dockViewLayout'] & { activeGroup?: string };

export interface MobileTradingPanelDispatcherProps {
  layout: LayoutSpec;
  panel: GroupviewPanelState;
}

/**
 * Maps a trading `panel.params.type` to its mobile component. Each `case` narrows `panelConfig` to the
 * matching discriminated-union member, so child components receive a specific `PanelConfig` variant
 * without additional casts. Unsupported panel types redirect to the first mobile-supported panel in the layout.
 */
export function MobileTradingPanelDispatcher({ layout, panel }: MobileTradingPanelDispatcherProps) {
  const panelConfig = getPanelConfig(panel);

  const content = (() => {
    switch (panelConfig?.type) {
      case 'ordersBlotter':
        return (
          <MobileOrdersBlotter sectionId={TRADING_SECTION_ID} layout={layout} panel={panel} panelConfig={panelConfig} />
        );
      case 'tradesBlotter':
        return (
          <MobileTradesBlotter sectionId={TRADING_SECTION_ID} layout={layout} panel={panel} panelConfig={panelConfig} />
        );
      case 'positionsBlotter':
        return (
          <MobilePositionsBlotter
            sectionId={TRADING_SECTION_ID}
            layout={layout}
            panel={panel}
            panelConfig={panelConfig}
          />
        );
      case 'balancesBlotter':
        return (
          <MobileBalancesBlotter
            sectionId={TRADING_SECTION_ID}
            layout={layout}
            panel={panel}
            panelConfig={panelConfig}
          />
        );
      case 'creditBlotter':
        return (
          <MobileCreditBlotter sectionId={TRADING_SECTION_ID} layout={layout} panel={panel} panelConfig={panelConfig} />
        );
      // case 'otcDerivatives':
      //   return <MobileOTCDerivativesBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      // case 'loans':
      //   return <MobileLoansBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'transfersBlotter':
        return (
          <MobileTransfersBlotter
            sectionId={TRADING_SECTION_ID}
            layout={layout}
            panel={panel}
            panelConfig={panelConfig}
          />
        );
      // case 'reconMismatches':
      //   return <MobileReconMismatchesBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      // case 'hedgePositionStatus':
      //   return <MobileHedgePositionStatusBlotter layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'marketCards':
        return (
          <MobileMarketPanel layout={layout} panel={panel}>
            <MobileMarketCards panel={panel} panelConfig={panelConfig} />
          </MobileMarketPanel>
        );
      case 'predictionMarkets':
        return <MobilePredictionMarkets layout={layout} panel={panel} panelConfig={panelConfig} />;
      case 'watchlist':
        return (
          <MobileMarketPanel layout={layout} panel={panel}>
            <MobileWatchlistPanel panel={panel} panelConfig={panelConfig} />
          </MobileMarketPanel>
        );
      default:
        return <UnsupportedPanelRedirect layout={layout} panel={panel} />;
    }
  })();

  return <Suspense fallback={<LoaderTalos />}>{content}</Suspense>;
}

function UnsupportedPanelRedirect({ layout, panel }: { layout: LayoutSpec; panel: GroupviewPanelState }) {
  const { enablePredictionMarkets } = useFeatureFlag();
  const targetPanel = getFirstMobileFriendlyPanel(
    layout,
    enablePredictionMarkets,
    getMobileFriendlyPanelFallbackScope(panel)
  );

  if (targetPanel == null) {
    return <TradingPlaceholder label={NO_MOBILE_FRIENDLY_PANELS_TITLE} message={NO_MOBILE_FRIENDLY_PANELS_MESSAGE} />;
  }

  return <Navigate to={getTradingPanelRoute(layout, targetPanel.id)} replace />;
}

export function getFirstMobileFriendlyPanel(
  layout: LayoutSpec,
  enablePredictionMarkets: boolean,
  scope: MobileFriendlyPanelScope = 'any'
) {
  return Object.values(layout.dockViewLayout.panels).find(panel =>
    isMobileFriendlyPanel(panel, enablePredictionMarkets, scope)
  );
}

export function getMobileFriendlyPanelForActiveLayoutPanel(layout: LayoutSpec, enablePredictionMarkets: boolean) {
  const activePanel = getFirstActiveLayoutPanel(layout);
  if (activePanel == null) {
    return getFirstMobileFriendlyPanel(layout, enablePredictionMarkets, 'market');
  }

  if (isMobileFriendlyPanel(activePanel, enablePredictionMarkets, 'any')) {
    return activePanel;
  }

  return getFirstMobileFriendlyPanel(layout, enablePredictionMarkets, getMobileFriendlyPanelFallbackScope(activePanel));
}

function getFirstActiveLayoutPanel(layout: LayoutSpec): GroupviewPanelState | undefined {
  const activeGroupId = (layout.dockViewLayout as DockviewLayoutWithActiveGroup).activeGroup;
  const activePanelId =
    activeGroupId != null ? getLayoutGroupById(layout.dockViewLayout, activeGroupId)?.data.activeView : undefined;
  return activePanelId != null ? layout.dockViewLayout.panels[activePanelId] : undefined;
}

function getMobileFriendlyPanelFallbackScope(panel: GroupviewPanelState): MobileFriendlyPanelScope {
  const panelType = getPanelConfig(panel)?.type as PanelType | undefined;
  return panelType != null && PANEL_COMPONENTS_MAP[panelType]?.group?.type === 'blotters' ? 'blotter' : 'market';
}

function isMobileFriendlyPanel(
  panel: GroupviewPanelState,
  enablePredictionMarkets: boolean,
  scope: MobileFriendlyPanelScope
) {
  const panelType = getPanelConfig(panel)?.type as PanelType | undefined;
  if (panelType == null) {
    return false;
  }

  if (scope === 'market') {
    return isEnabledMarketTabPanelType(panelType, enablePredictionMarkets);
  }

  const isSupportedPanel =
    MOBILE_SUPPORTED_PANEL_TYPES.includes(panelType) && (enablePredictionMarkets || panelType !== 'predictionMarkets');
  if (scope === 'blotter') {
    return isSupportedPanel && PANEL_COMPONENTS_MAP[panelType]?.group?.type === 'blotters';
  }

  return isSupportedPanel;
}

export default MobileTradingPanelDispatcher;
